import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Loading from '../components/icon/loading';
import { resolveAvatarUrl, handleAvatarError } from '../utils/profilePicUrl';
import { getAuthorDisplayName } from '../utils/displayName';
import { getApiBaseUrl } from '../config/api';
import ReportModal from './shared/ReportModal';
import { confirmAndBlock, unblockUser } from '../utils/moderation';
import './UserProfile.css';

const UserProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const API_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL;

  const getViewer = () => {
    try {
      return JSON.parse(localStorage.getItem('forumUser') || localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  };

  const viewer = getViewer();
  const viewerId = viewer?.id ? Number(viewer.id) : 0;

  const fetchProfile = useCallback(async () => {
    try {
      setError(null);
      const qs = new URLSearchParams({ user_id: userId });
      if (viewerId) qs.set('viewer_id', String(viewerId));
      const res = await fetch(`${API_URL}/get_user_profile.php?${qs}`);
      const data = await res.json();

      if (data.success) {
        setProfile(data.user);
      } else {
        setError(data.message || 'Failed to load profile.');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('An error occurred while loading the profile.');
    }
  }, [userId, API_URL, viewerId]);

  useEffect(() => {
    if (userId) {
      setProfile(null);
      fetchProfile();
    }
  }, [userId, fetchProfile]);

  const postJson = async (endpoint, body) => {
    const res = await fetch(`${API_URL}/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  const handleConnect = async () => {
    if (!viewerId || actionBusy) return;
    setActionBusy(true);
    try {
      const data = await postJson('send_connection_request.php', {
        requester_id: viewerId,
        addressee_id: Number(userId),
      });
      if (data.success || data.connection_status) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not send request.');
      }
    } catch (err) {
      alert('Network error sending request.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRespond = async (action) => {
    if (!viewerId || actionBusy) return;
    setActionBusy(true);
    try {
      const data = await postJson('respond_connection.php', {
        user_id: viewerId,
        connection_id: profile.connection_id,
        requester_id: Number(userId),
        action,
      });
      if (data.success) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not update request.');
      }
    } catch (err) {
      alert('Network error.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleWithdraw = async () => {
    if (!viewerId || actionBusy) return;
    setActionBusy(true);
    try {
      const data = await postJson('withdraw_connection.php', {
        user_id: viewerId,
        connection_id: profile.connection_id,
        addressee_id: Number(userId),
      });
      if (data.success) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not withdraw.');
      }
    } catch (err) {
      alert('Network error.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!viewerId || actionBusy) return;
    if (!window.confirm('Remove this connection?')) return;
    setActionBusy(true);
    try {
      const data = await postJson('remove_connection.php', {
        user_id: viewerId,
        connection_id: profile.connection_id,
        target_id: Number(userId),
      });
      if (data.success) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not remove connection.');
      }
    } catch (err) {
      alert('Network error.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleFollowToggle = async () => {
    if (!viewerId || actionBusy) return;
    setActionBusy(true);
    try {
      const endpoint = profile.is_following ? 'unfollow_user.php' : 'follow_user.php';
      const data = await postJson(endpoint, {
        follower_id: viewerId,
        following_id: Number(userId),
      });
      if (data.success) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not update follow.');
      }
    } catch (err) {
      alert('Network error.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleBlock = async () => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const blocked = await confirmAndBlock(viewer, {
        id: Number(userId),
        name: getAuthorDisplayName(profile),
      });
      if (blocked) await fetchProfile();
    } finally {
      setActionBusy(false);
    }
  };

  const handleUnblock = async () => {
    if (!viewerId || actionBusy) return;
    setActionBusy(true);
    try {
      const data = await unblockUser(viewerId, Number(userId));
      if (data.success) {
        await fetchProfile();
      } else {
        alert(data.message || 'Could not unblock.');
      }
    } finally {
      setActionBusy(false);
    }
  };

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>{error}</div>;
  }

  if (!profile) {
    return (
      <div style={{ textAlign: 'center', marginTop: '40px' }}>
        <Loading />
      </div>
    );
  }

  const profilePicUrl = resolveAvatarUrl(profile, API_URL);
  const displayName = getAuthorDisplayName(profile);
  const skills = Array.isArray(profile.skills) ? profile.skills : [];
  const experience = Array.isArray(profile.experience) ? profile.experience : [];
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  const isSelf = profile.connection_status === 'self' || (viewerId && Number(userId) === viewerId);
  const status = profile.connection_status || 'none';
  const isBlocked = Boolean(profile.is_blocked);
  const profileIsAdmin = String(profile.typeofuser || '').toLowerCase() === 'admin';

  const formatPeriod = (item) => {
    const start = item.start_date || '';
    const end = item.is_current ? 'Present' : item.end_date || '';
    if (start && end) return `${start} – ${end}`;
    if (start) return start;
    if (end) return end;
    return '';
  };

  return (
    <div className="up-page">
      <div className="up-card">
        <div className="up-cover" aria-hidden="true" />

        <div className="up-header">
          <div className="up-avatar-wrap">
            <img
              src={profilePicUrl}
              alt={`${displayName}'s avatar`}
              className="up-avatar"
              onError={handleAvatarError}
            />
            <span className="up-avatar-ring" aria-hidden="true" />
          </div>

          <h1 className="up-name">{displayName}</h1>

          {profile.position && <p className="up-handle" style={{ color: '#1e88e5', fontWeight: 500 }}>{profile.position}</p>}

          {profile.username && <p className="up-handle">@{profile.username}</p>}

          <div className="up-stats">
            <Link to={isSelf ? '/network?tab=connections' : '#'} className="up-stat">
              <strong>{profile.connection_count ?? 0}</strong>
              <span>Connections</span>
            </Link>
            <span className="up-stat">
              <strong>{profile.follower_count ?? 0}</strong>
              <span>Followers</span>
            </span>
            <span className="up-stat">
              <strong>{profile.following_count ?? 0}</strong>
              <span>Following</span>
            </span>
          </div>

          <div className="up-meta">
            {profile.country && (
              <span>
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
                {profile.country}
              </span>
            )}
            {profile.email && (
              <span>
                <i className="fas fa-envelope" aria-hidden="true" />
                {profile.email}
              </span>
            )}
          </div>

          <div className="up-actions">
            {!isSelf && viewerId > 0 && isBlocked && (
              <>
                <p className="up-blocked-note">
                  <i className="fas fa-ban" aria-hidden="true" /> You blocked this member.
                </p>
                <button
                  type="button"
                  className="up-btn up-btn-ghost"
                  disabled={actionBusy}
                  onClick={handleUnblock}
                >
                  <i className="fas fa-unlock" aria-hidden="true" />
                  Unblock
                </button>
              </>
            )}
            {!isSelf && viewerId > 0 && !isBlocked && (
              <>
                {status === 'none' && (
                  <button
                    type="button"
                    className="up-btn up-btn-primary"
                    disabled={actionBusy}
                    onClick={handleConnect}
                  >
                    <i className="fas fa-user-plus" aria-hidden="true" />
                    Connect
                  </button>
                )}
                {status === 'pending_sent' && (
                  <button
                    type="button"
                    className="up-btn up-btn-ghost"
                    disabled={actionBusy}
                    onClick={handleWithdraw}
                  >
                    <i className="fas fa-clock" aria-hidden="true" />
                    Pending — Withdraw
                  </button>
                )}
                {status === 'pending_received' && (
                  <>
                    <button
                      type="button"
                      className="up-btn up-btn-primary"
                      disabled={actionBusy}
                      onClick={() => handleRespond('accept')}
                    >
                      <i className="fas fa-check" aria-hidden="true" />
                      Accept
                    </button>
                    <button
                      type="button"
                      className="up-btn up-btn-ghost"
                      disabled={actionBusy}
                      onClick={() => handleRespond('decline')}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                      Decline
                    </button>
                  </>
                )}
                {status === 'connected' && (
                  <>
                    <button
                      type="button"
                      className="up-btn up-btn-primary"
                      onClick={() => {
                        window.dispatchEvent(
                          new CustomEvent('cool:open-chat', {
                            detail: { userId: Number(userId) },
                          })
                        );
                      }}
                    >
                      <i className="fas fa-comment-dots" aria-hidden="true" />
                      Message
                    </button>
                    <button
                      type="button"
                      className="up-btn up-btn-connected"
                      disabled={actionBusy}
                      onClick={handleRemove}
                    >
                      <i className="fas fa-user-check" aria-hidden="true" />
                      Connected — Remove
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className={`up-btn ${profile.is_following ? 'up-btn-ghost' : 'up-btn-follow'}`}
                  disabled={actionBusy}
                  onClick={handleFollowToggle}
                >
                  <i
                    className={`fas ${profile.is_following ? 'fa-user-minus' : 'fa-rss'}`}
                    aria-hidden="true"
                  />
                  {profile.is_following ? 'Following' : 'Follow'}
                </button>
              </>
            )}
            {isSelf && (
              <Link to="/network" className="up-btn up-btn-primary">
                <i className="fas fa-users" aria-hidden="true" />
                My Network
              </Link>
            )}
            {profile.email && !isBlocked && (
              <a href={`mailto:${profile.email}`} className="up-btn up-btn-ghost">
                <i className="fas fa-paper-plane" aria-hidden="true" />
                Email Me
              </a>
            )}
          </div>

          {!isSelf && viewerId > 0 && (
            <div className="up-safety">
              <button type="button" className="up-safety-btn" onClick={() => setReportOpen(true)}>
                <i className="fas fa-flag" aria-hidden="true" /> Report
              </button>
              {!isBlocked && !profileIsAdmin && (
                <button
                  type="button"
                  className="up-safety-btn up-safety-btn--danger"
                  disabled={actionBusy}
                  onClick={handleBlock}
                >
                  <i className="fas fa-ban" aria-hidden="true" /> Block
                </button>
              )}
            </div>
          )}

          <ReportModal
            open={reportOpen}
            target={{ type: 'user', id: Number(userId), name: displayName }}
            user={viewer}
            onClose={() => setReportOpen(false)}
          />

          {fullName && fullName !== displayName && !profile.privacy_locked && (
            <p className="up-fullname">Full name: {fullName}</p>
          )}
        </div>

        {profile.privacy_locked ? (
          <div className="up-private">
            <div className="up-private-icon" aria-hidden="true">
              <i className="fas fa-lock" />
            </div>
            <h2>
              {profile.privacy_reason === 'connections'
                ? 'This profile is for connections only'
                : 'This profile is private'}
            </h2>
            <p>
              {profile.privacy_reason === 'connections'
                ? 'Only accepted connections can see this learner’s information. Send a connection request to request access.'
                : 'This learner has chosen to keep their profile information private.'}
            </p>
          </div>
        ) : (
        <div className="up-body">
          {profile.bio && (
            <section className="up-section">
              <h2 className="up-section-title">
                <i className="fas fa-user" aria-hidden="true" />
                About
              </h2>
              <p className="up-bio">{profile.bio}</p>
            </section>
          )}

          <section className="up-section">
            <h2 className="up-section-title">
              <i className="fas fa-bolt" aria-hidden="true" />
              Skills
            </h2>
            {profile.skills_hidden ? (
              <p className="up-empty">Skills are hidden by this learner’s privacy settings.</p>
            ) : skills.length === 0 ? (
              <p className="up-empty">No skills listed yet.</p>
            ) : (
              <div className="up-skills">
                {skills.map((s) => (
                  <span key={s.id || s.skill_name} className="up-skill">
                    {s.skill_name || s}
                  </span>
                ))}
              </div>
            )}
          </section>

          <section className="up-section">
            <h2 className="up-section-title">
              <i className="fas fa-briefcase" aria-hidden="true" />
              Work Experience
            </h2>
            {profile.experience_hidden ? (
              <p className="up-empty">Work experience is hidden by this learner’s privacy settings.</p>
            ) : experience.length === 0 ? (
              <p className="up-empty">No work experience listed yet.</p>
            ) : (
              <div className="up-timeline">
                {experience.map((item, index) => (
                  <article key={item.id || index} className="up-exp">
                    <h3 className="up-exp-title">{item.job_title}</h3>
                    <p className="up-exp-company">{item.company}</p>
                    {formatPeriod(item) && (
                      <div className="up-exp-period">
                        <i className="far fa-calendar-alt" aria-hidden="true" />
                        {formatPeriod(item)}
                      </div>
                    )}
                    {item.description && (
                      <p className="up-exp-desc">{item.description}</p>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
